import { useRef } from "react";

interface OtpInputProps {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  autoFocus?: boolean;
}

export function OtpInput({ value, onChange, length = 6, autoFocus }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  const focus = (i: number) =>
    refs.current[Math.max(0, Math.min(i, length - 1))]?.focus();

  const emit = (arr: string[]) => onChange(arr.join(""));

  const handleChange = (i: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = digit;
    emit(next);
    if (digit) focus(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) {
        next[i] = "";
        emit(next);
      } else {
        focus(i - 1);
        const prev = [...digits];
        prev[i - 1] = "";
        emit(prev);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault(); focus(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault(); focus(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const clean = e.clipboardData
      .getData("text")
      .replace(/\s/g, "")
      .replace(/\D/g, "")
      .slice(0, length);
    const next = Array.from({ length }, (_, i) => clean[i] ?? "");
    emit(next);
    focus(Math.min(clean.length, length - 1));
  };

  return (
    <div className="flex gap-2.5 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          autoComplete="one-time-code"
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          className="w-11 h-13 sm:w-12 text-center text-xl font-bold text-gray-900 dark:text-white bg-white dark:bg-white/5 border-2 border-gray-200 dark:border-white/15 rounded-xl transition-all duration-150 focus:outline-none focus:border-[#9b87f5] focus:ring-2 focus:ring-[#9b87f5]/20 caret-transparent"
          style={{ height: "3.25rem" }}
        />
      ))}
    </div>
  );
}
